import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import grey from "@mui/material/colors/grey";
import { tableBorderStyle } from "../data";

const getRowSpanMap = (rows, rowSpanColumns) => {
	const spanMap = {};
	const groupEndRows = new Set();

	Object.keys(rowSpanColumns).forEach((colKey) => {
		spanMap[colKey] = {};
		let lastValue = null;
		let startIndex = 0;

		for (let i = 0; i < rows.length; i++) {
			const currentValue = rows[i][colKey];
			const isSame = currentValue === lastValue;

			if (isSame) {
				spanMap[colKey][startIndex].rowSpan += 1;
				spanMap[colKey][i] = { skip: true };
				spanMap[colKey][startIndex].groupEndIndex = i;
			} else {
				lastValue = currentValue;
				startIndex = i;
				spanMap[colKey][i] = { rowSpan: 1, groupEndIndex: i };
			}
		}

		// Collect group end indices for multi-row groups, excluding the last one
		const allGroups = Object.entries(spanMap[colKey])
			.filter(([_, meta]) => !meta.skip && meta.rowSpan >= 1)
			.map(([startIndex, meta]) => ({
				startIndex: +startIndex,
				endIndex: meta.groupEndIndex,
			}));

		if (allGroups.length > 1) {
			for (let i = 0; i < allGroups.length - 1; i++) {
				groupEndRows.add(allGroups[i].endIndex);
			}
		}
	});

	return { spanMap, groupEndRows };
};

const TableDisplay = ({
	columns,
	rows,
	disableFirstRow = false,
	onRowClick,
	rowHeight = "36px",
	columnWidth,
	minWidth = 70,
	disableTableBorder = false,
	rowSpanColumns = {},
	customGroupBorderBottom = `2px solid ${grey[600]}`,
	firstRowStyle = { background: grey[50], fontWeight: "bold", color: grey[700] },
}) => {
	if (!Array.isArray(rows) || rows.length === 0) {
		return <div>Failed to Display Table</div>;
	}

	const tableBorder = disableTableBorder ? "0px" : disableFirstRow ? `1px solid ${grey[400]}` : tableBorderStyle;

	const cellBaseStyle = {
		boxSizing: "border-box",
		textTransform: "uppercase",
		padding: "0px",
		minWidth,
		height: rowHeight,
		width: columnWidth ? columnWidth : `${100 / columns.length}%`,
		borderRight: tableBorder,
		borderBottom: tableBorder,
	};

	const headerCellStyle = {
		...cellBaseStyle,
		background: grey[300],
		fontWeight: 600,
	};

	const rowStyleBase = {
		boxSizing: "border-box",
		height: rowHeight,
		overflow: "hidden",
		padding: "0px",
		margin: "0px",
	};

	const { spanMap: rowSpanMap, groupEndRows } = getRowSpanMap(rows, rowSpanColumns);

	return (
		<TableContainer sx={{ border: tableBorder }}>
			<Table>
				<TableHead>
					<TableRow sx={rowStyleBase}>
						{columns.map((col) => (
							<TableCell key={col.key} align="center" sx={headerCellStyle} component="th" scope="col">
								{typeof col.renderHeader === "function" ? col.renderHeader({ col }) : col.name}
							</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map((row, rowIndex) => {
						return (
							<TableRow
								key={rowIndex}
								onClick={() => typeof onRowClick === "function" && onRowClick(row)}
								sx={{
									...rowStyleBase,
								}}>
								{columns.map((col) => {
									const colKey = col.key;
									const isSpanning = rowSpanColumns[colKey];
									const spanMeta = rowSpanMap[colKey]?.[rowIndex];

									if (isSpanning && spanMeta?.skip) return null;

									const isRowSpanGroupStart = isSpanning && spanMeta?.rowSpan > 1;
									const spanEndsAt = spanMeta?.groupEndIndex;

									const isSpannedGroupCell = isRowSpanGroupStart && groupEndRows.has(spanEndsAt);

									const isGroupEndCell =
										groupEndRows.has(rowIndex) &&
										(!isSpanning || (spanMeta?.groupEndIndex === rowIndex && spanMeta?.rowSpan >= 1));

									const borderBottom = isSpannedGroupCell || isGroupEndCell ? customGroupBorderBottom : tableBorder;

									return (
										<TableCell
											key={colKey}
											align="center"
											component="td"
											scope="row"
											rowSpan={isSpanning ? spanMeta?.rowSpan : undefined}
											sx={{
												...cellBaseStyle,
												borderBottom,
												...(rowIndex === 0 && !disableFirstRow ? firstRowStyle : {}),
											}}>
											{typeof col.renderCell === "function"
												? col.renderCell({ row, field: colKey })
												: row[colKey]}
										</TableCell>
									);
								})}
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

export default TableDisplay;
